import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { CheckCircle2, FileText } from "lucide-react";

export default function OfertaMontagem() {
  const { token } = useParams();
  const [oferta, setOferta] = useState(null);
  const [fase, setFase] = useState("carregando"); // carregando, oferta, contrato, aceito, encerrada, erro
  const [contrato, setContrato] = useState(null);
  const [liConcordo, setLiConcordo] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/oferta/${token}`).then(r => {
      setOferta(r.data);
      if (r.data.status === "AGUARDANDO_PAGAMENTO") setFase("aceito");
      else if (["DECLINADO_PELO_CLIENTE", "OFERTA_EXPIRADA_SEM_RESPOSTA"].includes(r.data.status)) setFase("encerrada");
      else if (r.data.status === "CONTRATO_PENDENTE") abrirContrato();
      else setFase("oferta");
    }).catch(() => setFase("erro"));
    const pg = new URLSearchParams(window.location.search).get("pagamento");
    if (pg === "expirado") toast.info("O link de pagamento anterior expirou. Toque em PAGAR AGORA para gerar um novo — sem custo extra.", { duration: 9000 });
    else if (pg === "cancelado") toast.info("Pagamento cancelado. Quando quiser concluir, toque em PAGAR AGORA.", { duration: 8000 });
    else if (pg === "sucesso") toast.success("Pagamento em confirmação — esta página libera o Portal do Pedido automaticamente.", { duration: 9000 });
    // eslint-disable-next-line
  }, [token]);

  const responder = async (interesse) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/oferta/${token}/resposta`, { interesse });
      if (r.data.status === "DECLINADO_PELO_CLIENTE") { setFase("encerrada"); toast.success("Tudo bem! Obrigado pelo retorno."); }
      else await abrirContrato();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const abrirContrato = async () => {
    try {
      const r = await axios.get(`${API}/oferta/${token}/contrato`);
      setContrato(r.data);
      setFase("contrato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const aceitar = async () => {
    if (!liConcordo) { toast.error("Marque que leu e concorda com o contrato"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/oferta/${token}/aceite-contrato`);
      toast.success(r.data.mensagem);
      setFase("aceito");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (fase === "carregando") return <Tela><p className="text-center text-slate-400">Carregando…</p></Tela>;
  if (fase === "erro") return <Tela><h1 className="font-bold text-xl text-center">Oferta não encontrada</h1><p className="text-sm text-slate-400 text-center mt-2">O link pode ter expirado. Fale com a loja.</p></Tela>;
  if (fase === "encerrada") return (
    <Tela>
      <h1 className="font-bold text-xl text-center" data-testid="oferta-encerrada">Oferta encerrada</h1>
      <p className="text-sm text-slate-400 text-center mt-2">Esta oferta não está mais disponível. Se desejar contratar a montagem, entre em contato com a loja.</p>
    </Tela>
  );
  if (fase === "aceito") return (
    <Tela>
      <div className="text-center" data-testid="oferta-aceita">
        <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" />
        <h1 className="font-bold text-2xl">Contrato aceito! 🎉</h1>
        <p className="text-sm text-slate-400 mt-3 max-w-md mx-auto">
          Protocolo <strong className="text-white">{oferta?.protocolo}</strong>.
          Conclua o pagamento por Pix ou cartão para abrirmos seu pedido — a confirmação é automática.
        </p>
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            for (let tentativa = 1; tentativa <= 2; tentativa++) {
              try {
                const r = await axios.post(`${API}/particular/${token}/pagar`);
                if (r.data.pago) { window.location.reload(); return; }
                window.location.assign(r.data.checkout_url);
                return;
              } catch (e) {
                if (tentativa === 1) { await new Promise(res => setTimeout(res, 1500)); continue; }
                const detail = e?.response?.data?.detail;
                toast.error(typeof detail === "string" && detail.length < 160
                  ? detail
                  : "Não foi possível iniciar o pagamento agora. Aguarde alguns segundos e toque em PAGAR novamente.");
              }
            }
            setBusy(false);
          }}
          className="mt-6 px-8 py-4 rounded-xl bg-[#FF6700] hover:bg-[#e05a00] text-white font-bold text-base disabled:opacity-60"
          data-testid="oferta-pagar-btn">
          💳 {busy ? "ABRINDO PAGAMENTO…" : "PAGAR AGORA (PIX OU CARTÃO)"}
        </button>
        <p className="text-[11px] text-slate-500 mt-3">Pagamento seguro processado pelo Asaas. Após pagar, esta página libera o Portal do Pedido automaticamente.</p>
      </div>
    </Tela>
  );

  if (fase === "contrato") return (
    <Tela larga>
      <div className="flex items-center gap-2 mb-4">
        <FileText size={20} className="text-[#FF6700]" />
        <h1 className="font-bold text-xl">Contrato de Montagem — leia antes do pagamento</h1>
      </div>
      <div className="bg-white text-slate-800 rounded-xl p-5 max-h-[55vh] overflow-y-auto text-[13px] whitespace-pre-wrap leading-relaxed"
        data-testid="contrato-texto">
        {contrato?.texto}
      </div>
      <label className="flex items-start gap-2 mt-4 text-sm cursor-pointer">
        <input type="checkbox" checked={liConcordo} onChange={e => setLiConcordo(e.target.checked)}
          className="mt-1" data-testid="contrato-checkbox" />
        <span>Li e aceito o contrato (versão {contrato?.versao}). Estou ciente de que o aceite será registrado com data, hora, IP e dispositivo.</span>
      </label>
      <Button onClick={aceitar} disabled={busy || !liConcordo}
        className="mt-4 w-full h-12 bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold"
        data-testid="contrato-aceitar-btn">
        {busy ? "Registrando…" : "Aceitar contrato e prosseguir para pagamento"}
      </Button>
    </Tela>
  );

  return (
    <Tela larga>
      <div data-testid="oferta-detalhe">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6700]">Oferta de montagem profissional</div>
        <h1 className="font-bold text-2xl mt-2">Olá, {oferta?.cliente_nome?.split(" ")[0]}! 👋</h1>
        <p className="text-sm text-slate-400 mt-2">
          Somos parceiros das montagens da <strong className="text-white">{oferta?.loja}</strong>.
          Preparamos uma oferta para montar os produtos do seu pedido. A contratação é <strong className="text-white">opcional</strong>.
        </p>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mt-5 space-y-2">
          {oferta?.itens?.map((i, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span>{i.quantidade}x {i.produto}{i.marca ? ` · ${i.marca}` : ""}</span>
            </div>
          ))}
          <div className="border-t border-white/10 pt-3 flex justify-between items-center">
            <span className="text-sm text-slate-400">Valor total da montagem</span>
            <span className="font-bold text-2xl text-[#FF6700]" data-testid="oferta-valor">
              R$ {Number(oferta?.valor_montagem_total || 0).toFixed(2).replace(".", ",")}
            </span>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Protocolo {oferta?.protocolo} · válida até {oferta?.validade_oferta?.split("-").reverse().join("/")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          <Button onClick={() => responder(true)} disabled={busy}
            className="h-12 bg-[#FF6700] hover:bg-[#e65d00] text-white font-bold" data-testid="oferta-sim-btn">
            Tenho interesse
          </Button>
          <Button onClick={() => responder(false)} disabled={busy} variant="outline"
            className="h-12 border-white/20 text-white bg-transparent hover:bg-white/10" data-testid="oferta-nao-btn">
            Não tenho interesse
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3 text-center">
          "Tenho interesse" não é contratação — você verá o contrato completo e o pagamento antes de decidir.
        </p>
      </div>
    </Tela>
  );
}

function Tela({ children, larga }) {
  return (
    <div className="min-h-screen bg-[#0B1020] text-white flex items-center justify-center p-4">
      <div className={`w-full ${larga ? "max-w-2xl" : "max-w-md"} py-8`}>
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src="/brand/montaja-icon.png" alt="MontaJá" className="w-8 h-8" onError={e => { e.target.style.display = "none"; }} />
          <span className="font-extrabold text-lg">Monta<span className="text-[#FF6700]">Já</span></span>
        </div>
        {children}
      </div>
    </div>
  );
}
